"use client";

import * as React from "react";
import * as RAvatar from "@radix-ui/react-avatar";
import { cn } from "./cn";

const SIZE = {
  xs: "h-6 w-6 text-[10px]",
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-14 w-14 text-base",
  xl: "h-20 w-20 text-xl",
} as const;

export type AvatarProps = {
  name: string;
  src?: string | null;
  size?: keyof typeof SIZE;
  tone?: "ember" | "lime" | "ink" | "mist";
  className?: string;
};

export function Avatar({
  name,
  src,
  size = "md",
  tone = "mist",
  className,
}: AvatarProps) {
  const initials = React.useMemo(() => {
    const parts = name.trim().split(/\s+/);
    const take = parts.slice(-2);
    return (
      take
        .map((p) => p[0])
        .join("")
        .toUpperCase() || "?"
    );
  }, [name]);

  const fallbackBg =
    tone === "lime"
      ? "bg-lime text-paper"
      : tone === "ember"
        ? "bg-ember text-paper"
        : tone === "ink"
          ? "bg-ink text-paper"
          : "bg-smoke text-ink";

  return (
    <RAvatar.Root
      className={cn(
        "shrink-0 rounded-full inline-flex items-center justify-center overflow-hidden align-middle",
        SIZE[size],
        className,
      )}
    >
      {src ? (
        <RAvatar.Image
          src={src}
          alt={name}
          className="h-full w-full rounded-full object-cover"
        />
      ) : null}
      <RAvatar.Fallback
        delayMs={src ? 200 : 0}
        className={cn(
          "h-full w-full rounded-full font-bold inline-flex items-center justify-center",
          fallbackBg,
        )}
      >
        {initials}
      </RAvatar.Fallback>
    </RAvatar.Root>
  );
}
